import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Sidebar from '../components/Sidebar';
import { ticketService } from '../services/ticketService';
import { expenseService } from '../services/expenseService';
import { propertyService } from '../services/propertyService';
import { tenantService } from '../services/tenantService';
import { authService } from '../services/authService';
import { bidService } from '../services/bidService';
import { paymentService } from '../services/paymentService';

const AdminDashboard = () => {
  const { currentUser, userData } = useAuth();
  const { t } = useLanguage();
  const [platformStats, setPlatformStats] = useState({
    totalUsers: 0,
    totalProperties: 0,
    totalTickets: 0,
    totalExpenses: 0,
    totalBids: 0,
    activeTickets: 0,
    completedTickets: 0,
    totalRevenue: 0,
    pendingApprovals: 0
  });
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentTickets, setRecentTickets] = useState([]);
  const [recentProperties, setRecentProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTimeframe, setSelectedTimeframe] = useState('all');
  const [permissionErrors, setPermissionErrors] = useState([]);
  const [firestoreAccess, setFirestoreAccess] = useState(null);

  useEffect(() => {
    if (currentUser && userData) {
      // Check if user has admin role
      if (userData.role !== 'admin') {
        console.error('User does not have admin role:', userData.role);
        // You might want to redirect them or show an error message
        return;
      }
      
      // Check if user needs to change password
      if (userData.requiresPasswordChange) {
        alert('You need to change your password. Please sign out and sign back in to change your password.');
      }
      
      loadPlatformData();
    }
  }, [currentUser, userData, selectedTimeframe]);

  const createSampleData = async () => {
    try {
      const users = await authService.getAllUsers();
      const properties = await propertyService.getAllProperties();
      const tickets = await ticketService.getAllTickets();
      if (process.env.NODE_ENV === 'development') {
        console.log('Data counts:', { users: users.length, properties: properties.length, tickets: tickets.length });
      }
    } catch (error) {
      console.error('Error creating sample data:', error);
    }
  };

  const testFirestoreAccess = async () => {
    try {
      // Test basic collection access
      const collections = ['users', 'properties', 'tickets', 'expenses', 'bids', 'payments'];
      const results = {};
      
      for (const collectionName of collections) {
        try {
          const { collection, getDocs } = await import('firebase/firestore');
          const { db } = await import('../firebase');
          const querySnapshot = await getDocs(collection(db, collectionName));
          results[collectionName] = {
            accessible: true,
            count: querySnapshot.size,
            empty: querySnapshot.empty
          };
        } catch (error) {
          results[collectionName] = {
            accessible: false,
            error: error.message,
            code: error.code
          };
          console.error(`Error accessing collection ${collectionName}:`, error);
        }
      }
      setFirestoreAccess(results);
      
      // Check for permission errors
      const errors = [];
      for (const [collectionName, result] of Object.entries(results)) {
        if (!result.accessible && result.code === 'permission-denied') {
          errors.push(`Permission denied accessing ${collectionName} collection`);
        }
      }
      setPermissionErrors(errors);
      
      return results;
    } catch (error) {
      console.error('Error testing Firestore access:', error);
      setFirestoreAccess({ error: error.message });
      return null;
    }
  };

  const loadPlatformData = async () => {
    try {
      setLoading(true);
      const stats = await getPlatformStatistics();
      setPlatformStats(stats);
      const users = await getRecentUsers();
      setRecentUsers(users);
      const tickets = await getRecentTickets();
      setRecentTickets(tickets);
      const properties = await getRecentProperties();
      setRecentProperties(properties);
      setLoading(false);
    } catch (error) {
      console.error('Error loading platform data:', error);
      setLoading(false);
    }
  };

  const getPlatformStatistics = async () => {
    try {
      let users = [];
      try {
        users = await authService.getAllUsers();
      } catch (error) {
        console.error('Error fetching users:', error);
        if (error.code === 'permission-denied') {
          console.error('Permission denied accessing users collection');
        } else if (error.code === 'unavailable') {
          console.error('Firestore service unavailable');
        }
        users = [];
      }
      let filteredUsers = users;
      
      let properties = [];
      try {
        properties = await propertyService.getAllProperties();
      } catch (error) {
        console.error('Error fetching properties:', error);
        if (error.code === 'permission-denied') {
          console.error('Permission denied accessing properties collection');
        } else if (error.code === 'unavailable') {
          console.error('Firestore service unavailable');
        }
        properties = [];
      }
      let filteredProperties = properties;
      
      let tickets = [];
      try {
        tickets = await ticketService.getAllTickets();
      } catch (error) {
        console.error('Error fetching tickets:', error);
        if (error.code === 'permission-denied') {
          console.error('Permission denied accessing tickets collection');
        } else if (error.code === 'unavailable') {
          console.error('Firestore service unavailable');
        }
        tickets = [];
      }
      let filteredTickets = tickets;
      
      // Apply time-based filtering
      if (selectedTimeframe !== 'all') {
        const now = new Date();
        const filterDate = new Date();
        
        switch (selectedTimeframe) {
          case 'today':
            filterDate.setHours(0, 0, 0, 0);
            break;
          case 'week':
            filterDate.setDate(now.getDate() - 7);
            break;
          case 'month':
            filterDate.setMonth(now.getMonth() - 1);
            break;
          case 'year':
            filterDate.setFullYear(now.getFullYear() - 1);
            break;
        }
        
        filteredUsers = users.filter(user => {
          if (!user.createdAt) return false;
          return new Date(user.createdAt) >= filterDate;
        });
        filteredProperties = properties.filter(property => {
          if (!property.createdAt) return false;
          return new Date(property.createdAt) >= filterDate;
        });
        filteredTickets = tickets.filter(ticket => {
          if (!ticket.createdAt) return false;
          return new Date(ticket.createdAt) >= filterDate;
        });
      }
      
      const totalUsers = filteredUsers.length;
      const totalProperties = filteredProperties.length;
      const totalTickets = filteredTickets.length;
      const activeTickets = filteredTickets.filter(t => t.status === 'open' || t.status === 'in-progress').length;
      const completedTickets = filteredTickets.filter(t => t.status === 'completed');
      const completedTicketsCount = completedTickets.length;
      
      // Get all expenses
      let expenses = [];
      try {
        expenses = await expenseService.getAllExpenses();
      } catch (error) {
        console.error('Error fetching expenses:', error);
        if (error.code === 'permission-denied') {
          console.error('Permission denied accessing expenses collection');
        } else if (error.code === 'unavailable') {
          console.error('Firestore service unavailable');
        }
        expenses = [];
      }
      let filteredExpenses = expenses;
      
      if (selectedTimeframe !== 'all') {
        const now = new Date();
        const filterDate = new Date();
        
        switch (selectedTimeframe) {
          case 'today':
            filterDate.setHours(0, 0, 0, 0);
            break;
          case 'week':
            filterDate.setDate(now.getDate() - 7);
            break;
          case 'month':
            filterDate.setMonth(now.getMonth() - 1);
            break;
          case 'year':
            filterDate.setFullYear(now.getFullYear() - 1);
            break;
        }
        
        filteredExpenses = expenses.filter(expense => 
          expense.createdAt && new Date(expense.createdAt) >= filterDate
        );
      }
      
      const totalExpenses = filteredExpenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);
      
      // Get all bids
      let bids = [];
      try {
        bids = await bidService.getAllBids();
      } catch (error) {
        console.error('Error fetching bids:', error);
        if (error.code === 'permission-denied') {
          console.error('Permission denied accessing bids collection');
        } else if (error.code === 'unavailable') {
          console.error('Firestore service unavailable');
        }
        bids = [];
      }
      let filteredBids = bids;
      
      if (selectedTimeframe !== 'all') {
        const now = new Date();
        const filterDate = new Date();
        
        switch (selectedTimeframe) {
          case 'today':
            filterDate.setHours(0, 0, 0, 0);
            break;
          case 'week':
            filterDate.setDate(now.getDate() - 7);
            break;
          case 'month':
            filterDate.setMonth(now.getMonth() - 1);
            break;
          case 'year':
            filterDate.setFullYear(now.getFullYear() - 1);
            break;
        }
        
        filteredBids = bids.filter(bid => 
          bid.createdAt && new Date(bid.createdAt) >= filterDate
        );
      }
      
      const totalBids = filteredBids.length;
      
      // Calculate total revenue (from completed tickets and payments)
      let totalRevenue = 0;
      try {
        const payments = await paymentService.getAllPayments();
        let filteredPayments = payments;
        
        if (selectedTimeframe !== 'all') {
          const now = new Date();
          const filterDate = new Date();
          
          switch (selectedTimeframe) {
            case 'today':
              filterDate.setHours(0, 0, 0, 0);
              break;
            case 'week':
              filterDate.setDate(now.getDate() - 7);
              break;
            case 'month':
              filterDate.setMonth(now.getMonth() - 1);
              break;
            case 'year':
              filterDate.setFullYear(now.getFullYear() - 1);
              break;
          }
          
          filteredPayments = payments.filter(payment => 
            payment.createdAt && new Date(payment.createdAt) >= filterDate
          );
        }
        
        totalRevenue = filteredPayments
          .filter(p => p.status === 'completed')
          .reduce((sum, payment) => sum + (payment.amount || 0), 0);
      } catch (error) {
        console.error('Error fetching payments:', error);
        if (error.code === 'permission-denied') {
          console.error('Permission denied accessing payments collection');
        } else if (error.code === 'unavailable') {
          console.error('Firestore service unavailable');
        }
        // Fallback: estimate revenue from completed tickets
        totalRevenue = completedTicketsCount * 50; // Assuming $50 per completed ticket
      }
      
      let completedTicketExpenses = [];
      try {
        completedTicketExpenses = await Promise.all(
          completedTickets.map(async (ticket) => {
            try {
              const ticketExpenses = await expenseService.getTicketTotalExpenses(ticket.id);
              return ticketExpenses;
            } catch (error) {
              console.error('Error getting expenses for ticket:', ticket.id, error);
              return 0;
            }
          })
        );
        totalRevenue = completedTicketExpenses.reduce((sum, exp) => sum + exp, 0);
        console.log('Final total revenue:', totalRevenue);
      } catch (error) {
        console.error('Error calculating ticket expenses:', error);
        // Keep the revenue from payments if available
      }
      
      const pendingProviderApprovals = users.filter(u => u.role === 'serviceProvider' && !u.isApproved).length;
      return {
        totalUsers,
        totalProperties,
        totalTickets,
        totalExpenses,
        totalBids,
        activeTickets,
        completedTickets: completedTicketsCount,
        totalRevenue,
        pendingApprovals: pendingProviderApprovals
      };
    } catch (error) {
      console.error('Error getting platform statistics:', error);
      return {
        totalUsers: 0,
        totalProperties: 0,
        totalTickets: 0,
        totalExpenses: 0,
        totalBids: 0,
        activeTickets: 0,
        completedTickets: 0,
        totalRevenue: 0,
        pendingApprovals: 0
      };
    }
  };

  const getRecentUsers = async () => {
    try {
      const users = await authService.getAllUsers();
      let filteredUsers = users;
      
      // Apply time-based filtering
      if (selectedTimeframe !== 'all') {
        const now = new Date();
        const filterDate = new Date();
        
        switch (selectedTimeframe) {
          case 'today':
            filterDate.setHours(0, 0, 0, 0);
            break;
          case 'week':
            filterDate.setDate(now.getDate() - 7);
            break;
          case 'month':
            filterDate.setMonth(now.getMonth() - 1);
            break;
          case 'year':
            filterDate.setFullYear(now.getFullYear() - 1);
            break;
        }
        
        filteredUsers = users.filter(user => 
          user.createdAt && new Date(user.createdAt) >= filterDate
        );
      }
      
      return filteredUsers
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
        .slice(0, 5);
    } catch (error) {
      console.error('Error getting recent users:', error);
      return [];
    }
  };

  const getRecentTickets = async () => {
    try {
      const tickets = await ticketService.getAllTickets();
      let filteredTickets = tickets;
      
      // Apply time-based filtering
      if (selectedTimeframe !== 'all') {
        const now = new Date();
        const filterDate = new Date();
        
        switch (selectedTimeframe) {
          case 'today':
            filterDate.setHours(0, 0, 0, 0);
            break;
          case 'week':
            filterDate.setDate(now.getDate() - 7);
            break;
          case 'month':
            filterDate.setMonth(now.getMonth() - 1);
            break;
          case 'year':
            filterDate.setFullYear(now.getFullYear() - 1);
            break;
        }
        
        filteredTickets = tickets.filter(ticket => 
          ticket.createdAt && new Date(ticket.createdAt) >= filterDate
        );
      }
      
      return filteredTickets
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
        .slice(0, 5);
    } catch (error) {
      console.error('Error getting recent tickets:', error);
      return [];
    }
  };

  const getRecentProperties = async () => {
    try {
      const properties = await propertyService.getAllProperties();
      let filteredProperties = properties;
      
      // Apply time-based filtering
      if (selectedTimeframe !== 'all') {
        const now = new Date();
        const filterDate = new Date();
        
        switch (selectedTimeframe) {
          case 'today':
            filterDate.setHours(0, 0, 0, 0);
            break;
          case 'week':
            filterDate.setDate(now.getDate() - 7);
            break;
          case 'month':
            filterDate.setMonth(now.getMonth() - 1);
            break;
          case 'year':
            filterDate.setFullYear(now.getFullYear() - 1);
            break;
        }
        
        filteredProperties = properties.filter(property => 
          property.createdAt && new Date(property.createdAt) >= filterDate
        );
      }
      
      return filteredProperties
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
        .slice(0, 5);
    } catch (error) {
      console.error('Error getting recent properties:', error);
      return [];
    }
  };

  const handleUserAction = async (userId, action, userForDisplay) => {
    try {
      switch (action) {
        case 'suspend':
          await authService.suspendUser(userId);
          break;
        case 'activate':
          await authService.activateUser(userId);
          break;
        case 'delete':
          const userToDelete = userForDisplay || recentUsers.find(u => u.uid === userId);
          if (!userToDelete) {
            alert('User not found. Go to Users page to manage this user.');
            return;
          }
          
          const deleteConfirmation = window.confirm(
            `⚠️ COMPREHENSIVE USER DELETION ⚠️\n\n` +
            `Are you absolutely sure you want to delete user:\n` +
            `• Email: ${userToDelete.email}\n` +
            `• Name: ${userToDelete.displayName || 'N/A'}\n` +
            `• Role: ${userToDelete.role}\n\n` +
            `This will permanently delete:\n` +
            `• User account and profile\n` +
            `• All properties (if owner)\n` +
            `• All tickets and bids\n` +
            `• All payments and documents\n` +
            `• All chat messages and notifications\n` +
            `• All related data\n\n` +
            `This action CANNOT be undone!\n\n` +
            `Type "DELETE" to confirm:`
          );
          
          if (deleteConfirmation) {
            const userInput = prompt('Type "DELETE" to confirm permanent deletion:');
            if (userInput === 'DELETE') {
              try {
                const result = await authService.deleteUser(userId);
                alert(`✅ User deleted successfully!\n\n${result.message}\n\nNote: Firebase Auth account may still be active. Contact backend developer for complete removal.`);
              } catch (error) {
                console.error('User deletion failed:', error);
                alert(`❌ Failed to delete user: ${error.message}`);
                return; // Don't refresh data if deletion failed
              }
            } else {
              alert('Deletion cancelled. User input did not match "DELETE".');
              return;
            }
          } else {
            alert('User deletion cancelled.');
            return;
          }
          break;
        default:
          break;
      }
      loadPlatformData(); // Refresh data
    } catch (error) {
      console.error(`Error performing ${action} on user:`, error);
      alert(`Failed to ${action} user: ${error.message}`);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'suspended': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTicketStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-800';
      case 'in-progress': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Load real user management data from Firebase
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex">
        <Sidebar />
        <div className="flex-1 ml-72 -ml-8">
          {/* Header */}
          <header className="bg-white border-b border-gray-200 sticky top-0 z-10 px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Admin Dashboard</h1>
                <p className="text-sm text-gray-500 mt-0.5">Platform overview and quick actions</p>
              </div>
              <div className="text-sm text-gray-500">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}
              </div>
            </div>
          </header>

          <div className="p-8">

        {/* Admin Role Check */}
        {currentUser && userData && userData.role !== 'admin' && (
          <div className="mt-4 p-4 bg-yellow-100 border border-yellow-300 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-yellow-800">Access Restricted</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  You are logged in as {userData.role}, but this dashboard requires admin privileges.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Permission Errors Warning */}
        {permissionErrors.length > 0 && (
          <div className="mt-4 p-4 bg-red-100 border border-red-300 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-red-800">Permission Issues Detected</h3>
                <p className="text-sm text-red-700 mt-1">
                  Some collections are not accessible. Check the debug section below for details.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Timeframe Filter and Refresh */}
        <div className="mb-6 flex items-center space-x-4">
          <select
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="year">This Year</option>
          </select>
          
          <button
            onClick={loadPlatformData}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Loading...</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>Refresh</span>
              </>
            )}
          </button>
        </div>

        {/* Platform Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 bg-blue-100 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Users</p>
                <p className="text-2xl font-bold text-gray-900">{platformStats.totalUsers}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 bg-green-100 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Properties</p>
                <p className="text-2xl font-bold text-gray-900">{platformStats.totalProperties}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Tickets</p>
                <p className="text-2xl font-bold text-gray-900">{platformStats.activeTickets}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 bg-purple-100 rounded-lg">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">${platformStats.totalRevenue.toFixed(2)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 bg-red-100 rounded-lg">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Expenses</p>
                <p className="text-2xl font-bold text-gray-900">${platformStats.totalExpenses.toFixed(2)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 bg-indigo-100 rounded-lg">
                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Bids</p>
                <p className="text-2xl font-bold text-gray-900">{platformStats.totalBids}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="p-3 bg-teal-100 rounded-lg">
                <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Completed Tickets</p>
                <p className="text-2xl font-bold text-gray-900">{platformStats.completedTickets}</p>
              </div>
            </div>
          </div>

          <Link
            to="/admin/users?status=pending"
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:border-amber-300 hover:bg-amber-50/50 transition-colors block"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="p-3 bg-amber-100 rounded-lg">
                  <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Pending Approvals</p>
                  <p className="text-2xl font-bold text-gray-900">{platformStats.pendingApprovals}</p>
                  <p className="text-xs text-amber-600 mt-0.5">Service providers awaiting review</p>
                </div>
              </div>
              <span className="text-amber-600 text-sm font-medium">Review →</span>
            </div>
          </Link>
        </div>

        {/* Debug Information */}
        {process.env.NODE_ENV === 'development' && (
          <div className="bg-gray-100 rounded-lg p-4 mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Debug Information</h3>
            
            {/* Permission Errors */}
            {permissionErrors.length > 0 && (
              <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded">
                <h4 className="font-semibold text-red-800 mb-2">Permission Errors:</h4>
                <ul className="text-sm text-red-700">
                  {permissionErrors.map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Firestore Access Results */}
            {firestoreAccess && (
              <div className="mb-4 p-3 bg-blue-100 border border-blue-300 rounded">
                <h4 className="font-semibold text-blue-800 mb-2">Firestore Access Results:</h4>
                <pre className="text-xs overflow-auto">
                  {JSON.stringify(firestoreAccess, null, 2)}
                </pre>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p><strong>Loading State:</strong> {loading ? 'Yes' : 'No'}</p>
                <p><strong>Current Timeframe:</strong> {selectedTimeframe}</p>
                <p><strong>Platform Stats:</strong></p>
                <pre className="bg-white p-2 rounded text-xs overflow-auto">
                  {JSON.stringify(platformStats, null, 2)}
                </pre>
              </div>
              <div>
                <p><strong>Recent Users Count:</strong> {recentUsers.length}</p>
                <p><strong>Recent Tickets Count:</strong> {recentTickets.length}</p>
                <p><strong>Recent Properties Count:</strong> {recentProperties.length}</p>
                <button
                  onClick={async () => {
                    try {
                      const testData = {
                        users: await authService.getAllUsers(),
                        properties: await propertyService.getAllProperties(),
                        tickets: await ticketService.getAllTickets(),
                        expenses: await expenseService.getAllExpenses(),
                        bids: await bidService.getAllBids(),
                        payments: await paymentService.getAllPayments()
                      };
                      if (process.env.NODE_ENV === 'development') {
                        console.log('Test data:', {
                          users: testData.users.length,
                          properties: testData.properties.length,
                          tickets: testData.tickets.length
                        });
                      }
                    } catch (error) {
                      console.error('Error creating test data:', error);
                    }
                  }}
                  className="px-3 py-1 bg-yellow-500 text-white text-xs rounded hover:bg-yellow-600"
                >
                  Test Data
                </button>
                <button
                  onClick={testFirestoreAccess}
                  className="px-3 py-1 bg-purple-500 text-white text-xs rounded hover:bg-purple-600"
                >
                  Test Firestore
                </button>
                <button
                  onClick={createSampleData}
                  className="px-3 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600"
                >
                  Check Data
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link
              to="/admin/users"
              className="flex items-center justify-center p-4 border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-colors text-gray-700 font-medium"
            >
              <svg className="w-5 h-5 text-gray-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
              Manage Users
            </Link>
            <Link
              to="/admin/reports"
              className="flex items-center justify-center p-4 border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-colors text-gray-700 font-medium"
            >
              <svg className="w-5 h-5 text-gray-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              View Reports
            </Link>
            <Link
              to="/admin/system"
              className="flex items-center justify-center p-4 border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-colors text-gray-700 font-medium"
            >
              <svg className="w-5 h-5 text-gray-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              </svg>
              System Settings
            </Link>
            <button
              onClick={loadPlatformData}
              disabled={loading}
              className="flex items-center justify-center p-4 border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-colors text-gray-700 font-medium disabled:opacity-50"
            >
              <svg className={`w-5 h-5 text-gray-600 mr-2 flex-shrink-0 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh Data
            </button>
          </div>
        </div>

        {/* Recent Activity Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Users */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Recent Users</h3>
              <Link to="/admin/users" className="text-sm text-blue-600 hover:text-blue-800">View All</Link>
            </div>
            <div className="space-y-3">
              {recentUsers.map((user) => (
                <div key={user.uid} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                      <span className="text-xs font-medium text-gray-700">
                        {user.displayName?.charAt(0) || user.email?.charAt(0) || 'U'}
                      </span>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">{user.displayName || 'Unknown'}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(user.status || 'active')}`}>
                      {user.status || 'active'}
                    </span>
                    <div className="flex space-x-1">
                      <button
                        onClick={() => handleUserAction(user.uid, 'suspend')}
                        className="text-xs text-red-600 hover:text-red-800"
                        title="Suspend User"
                      >
                        ⏸️
                      </button>
                      <button
                        onClick={() => handleUserAction(user.uid, 'delete', user)}
                        className="text-xs text-red-600 hover:text-red-800"
                        title="Delete User"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Tickets */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Recent Tickets</h3>
              <Link to="/tickets" className="text-sm text-blue-600 hover:text-blue-800">View All</Link>
            </div>
            <div className="space-y-3">
              {recentTickets.map((ticket) => (
                <div key={ticket.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">{ticket.title}</p>
                      <p className="text-xs text-gray-500">{ticket.propertyName || 'No Property'}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTicketStatusColor(ticket.status)}`}>
                    {ticket.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* System Health */}
        <div className="mt-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">System Health</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h4 className="text-sm font-medium text-gray-900">Database</h4>
              <p className="text-xs text-gray-500">Healthy</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h4 className="text-sm font-medium text-gray-900">Authentication</h4>
              <p className="text-xs text-gray-500">Active</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h4 className="text-sm font-medium text-gray-900">Storage</h4>
              <p className="text-xs text-gray-500">Normal</p>
            </div>
          </div>
        </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
